// CRM Dashboard — analytics view only.
//   KPI cards with % change deltas, Revenue trend (area), Lead sources (pie),
//   Pipeline summary (horizontal bar), recent leads, recent activity, and
//   upcoming meetings.
// Backend (/dashboard/overview) returns the full payload in one call.

import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  Clock,
  Video,
  Target,
  Briefcase,
  Mail,
  ClipboardCheck,
  ArrowRight,
  Radar,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { useDashboard } from "../hooks/useActivities";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { formatCurrency as fmtCurrency, getCurrencySymbol } from "../lib/currency";
import ActivityRow from "../molecules/ActivityRow";
import { spring } from "../theme/motion";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmailFollowUpSettings, useEmailFollowUpAdminMonitor } from "../hooks/useFollowUps";
import { useMyCrmRole } from "../hooks/useTeamMembers";
import { Button } from "../atoms";
import PageHeader from "../organisms/PageHeader";
import EmailFollowUpAdminDrawer from "../overlays/EmailFollowUpAdminDrawer";
import { Badge, Tooltip as AntTooltip, Drawer as AntDrawer } from "antd";

function StatCard({ label, value, sub, icon: Icon, tone = "neutral", delay = 0, change }) {
  const accents = {
    primary: { bg: "var(--crm-primary-soft)", fg: "var(--crm-primary)" },
    success: { bg: "var(--crm-success-soft, rgba(16,185,129,0.12))", fg: "var(--crm-success, rgb(16,185,129))" },
    danger:  { bg: "var(--crm-danger-soft, rgba(239,68,68,0.12))", fg: "var(--crm-danger, rgb(239,68,68))" },
    neutral: { bg: "var(--color-bg-muted)", fg: "var(--color-text-secondary)" },
  };
  const a = accents[tone];
  const isUp = change?.direction === "up";
  const changeFg = change ? (isUp ? "var(--crm-success, rgb(16,185,129))" : "var(--crm-danger, rgb(239,68,68))") : null;
  const TrendIcon = isUp ? TrendingUp : TrendingDown;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...spring.smooth, delay }}
      style={{
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        padding: 18,
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        minWidth: 0,
      }}
    >
      <div style={{ width: 38, height: 38, borderRadius: 8, background: a.bg, color: a.fg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 4, }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
          {change && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: changeFg }}>
              <TrendIcon size={11} />
              {isUp ? "+" : "-"}{change.value}%
            </span>
          )}
          {sub && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{sub}</span>}
        </div>
      </div>
    </motion.div>
  );
}

function ChartCard({ title, subtitle, children, height = 280 }) {
  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 18, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ height, width: "100%" }}>{children}</div>
    </section>
  );
}

function formatMeetingTime(start, end) {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const now = new Date();
  const sameDay = s.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = s.toDateString() === tomorrow.toDateString();
  const dayLabel = sameDay ? "Today" : isTomorrow ? "Tomorrow" : s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const timeLabel = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const endLabel = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  return endLabel ? `${dayLabel} · ${timeLabel} – ${endLabel}` : `${dayLabel} · ${timeLabel}`;
}

function formatErr(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message;
  return err?.message || "Request failed";
}

export default function Dashboard({ onNavigateToPipeline, onNavigateToReview, onNavigateToRadar }) {
  const { data, isLoading, isError, error } = useDashboard();
  const { user } = useAuth();
  const { isAdmin: isCrmAdmin } = useMyCrmRole();
  const { data: fuSettingsData } = useEmailFollowUpSettings();
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [idleDrawerOpen, setIdleDrawerOpen] = useState(false);
  const isOrgAdmin = user?.role === "org_admin" || user?.role === "main_admin";
  const showFollowUpMonitor = isOrgAdmin && fuSettingsData?.settings?.dashboardMonitorEnabled === true;
  const { data: monitorCounts } = useEmailFollowUpAdminMonitor({ tab: "overdue", enabled: showFollowUpMonitor });
  const overdueCount = monitorCounts?.counts?.overdue || 0;
  const dueTodayCount = monitorCounts?.counts?.due_today || 0;
  const urgentCount = overdueCount + dueTodayCount;
  const { currency: orgCurrency } = useOrgCurrency();
  const fmt = (n) => fmtCurrency(n || 0, orgCurrency) || `${orgCurrency} 0`;
  const currencySymbol = getCurrencySymbol(orgCurrency);

  const counts = data?.counts || { totalLeads: 0, openLeads: 0, wonLeads: 0, lostLeads: 0, pipelines: 0 };
  const value = data?.value || { openValue: 0, wonValue: 0, lostValue: 0 };
  const changes = data?.changes || {};
  const staleLeads = data?.staleLeads || { total3: 0, total7: 0, total14: 0 };
  const recentActivities = data?.recentActivities || [];
  const revenueByMonth = data?.revenueByMonth || [];
  const leadSources = data?.leadSources || [];
  const pipelineStages = data?.pipelineStages || [];
  const upcomingMeetings = data?.upcomingMeetings || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader
        title="Overview"
        subtitle="Pipeline health, activity and revenue for the selected project"
        actions={<>
          {onNavigateToRadar ? (
            <Button variant="secondary" size="sm" leftIcon={<Radar size={14} />} onClick={onNavigateToRadar} title="Command Center — the pipeline as a deal radar">
              Command Center
            </Button>
          ) : null}
          {/* Idle leads button */}
          {(staleLeads.total3 > 0 || staleLeads.total7 > 0 || staleLeads.total14 > 0) && (
            <AntTooltip title="View idle leads by stage" mouseEnterDelay={0.4}>
              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                <Badge
                  count={staleLeads.total3 + staleLeads.total7 + staleLeads.total14}
                  overflowCount={999}
                  offset={[-2, 2]}
                  styles={{ indicator: { background: "rgb(220,38,38)", fontSize: 11, fontWeight: 600, minWidth: 18, height: 18, lineHeight: "18px", padding: "0 5px" } }}
                >
                  <Button variant="secondary" size="sm" leftIcon={<Clock size={14} />} onClick={() => setIdleDrawerOpen(true)}>
                    Idle leads
                  </Button>
                </Badge>
              </motion.div>
            </AntTooltip>
          )}

          {/* Follow-up monitor button */}
          {showFollowUpMonitor && (
            <AntTooltip
              title={urgentCount > 0 ? `${overdueCount} overdue · ${dueTodayCount} due today` : "All follow-ups on track"}
              mouseEnterDelay={0.3}
            >
              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                <Badge
                  count={urgentCount}
                  overflowCount={99}
                  offset={[-2, 2]}
                  styles={{ indicator: { background: urgentCount > 0 ? "rgb(220,38,38)" : undefined, boxShadow: urgentCount > 0 ? "0 0 0 2px #fff" : undefined, fontSize: 11, fontWeight: 600, minWidth: 18, height: 18, lineHeight: "18px", padding: "0 5px" } }}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Mail size={14} />}
                    onClick={() => setMonitorOpen(true)}
                    style={urgentCount > 0 ? { borderColor: "var(--crm-danger)", color: "var(--crm-danger)" } : undefined}
                  >
                    Follow-up monitor
                  </Button>
                </Badge>
              </motion.div>
            </AntTooltip>
          )}
        </>}
      />

      <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 24px" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 40, justifyContent: "center", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Loading overview…
          </div>
        )}
        {isError && (
          <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load dashboard</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{formatErr(error)}</div>
            </div>
          </div>
        )}

        {!isLoading && !isError && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              <StatCard label="Total leads" value={counts.totalLeads} sub="vs last month" change={changes.totalLeads} icon={Users} tone="primary" delay={0} />
              <StatCard label="Active deals" value={counts.openLeads} sub="in progress" change={changes.activeDeals} icon={Target} tone="primary" delay={0.05} />
              <StatCard label="Pipeline value" value={fmt(value.openValue)} sub="open pipeline" change={changes.pipelineValue} icon={Briefcase} tone="primary" delay={0.1} />
              <StatCard label="Won deals" value={counts.wonLeads} sub="this month" change={changes.wonDeals} icon={CheckCircle2} tone="success" delay={0.15} />
            </div>

            {/* Manager Weekly Review — preview card that deep-links into the
                Review tab. Reuses the already-loaded dashboard payload
                (stale + pipeline + counts) so it adds zero extra API calls. */}
            {onNavigateToReview && isCrmAdmin && (
              <motion.button
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...spring.smooth, delay: 0.2 }}
                whileHover={{ y: -2, boxShadow: "0 10px 28px rgba(0,0,0,0.08)" }}
                whileTap={{ scale: 0.995 }}
                onClick={() => onNavigateToReview()}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "linear-gradient(135deg, var(--crm-primary-soft) 0%, var(--color-bg-surface) 60%)",
                  border: "1px solid var(--color-border-subtle)",
                  borderRadius: 8,
                  padding: "18px 22px",
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 8, background: "var(--crm-primary-soft)", color: "var(--crm-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ClipboardCheck size={22} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "var(--crm-primary)", fontWeight: 600, marginBottom: 4 }}>
                    Manager Weekly Review
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 6 }}>
                    Deals per stage · stuck deals · neglected leads · lost reasons · email by VRM
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: "var(--color-text-secondary)", flexWrap: "wrap" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Target size={12} style={{ opacity: 0.7 }} />
                      <strong style={{ color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>{counts.openLeads}</strong> active
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Clock size={12} style={{ opacity: 0.7 }} />
                      <strong style={{ color: (staleLeads.total7 + staleLeads.total14) > 0 ? "var(--crm-danger, rgb(239,68,68))" : "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>{staleLeads.total7 + staleLeads.total14}</strong> stuck 7+ days
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <AlertCircle size={12} style={{ opacity: 0.7 }} />
                      <strong style={{ color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>{counts.lostLeads}</strong> lost
                    </span>
                  </div>
                </div>

                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--crm-primary)", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                  Open review
                  <ArrowRight size={15} />
                </div>
              </motion.button>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, alignItems: "stretch" }}>
              <ChartCard title="Revenue trend" subtitle="Last 6 months · pipeline value created" height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueByMonth} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--crm-primary)" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="var(--crm-primary)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-subtle)" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 11, fontWeight: 600 }} dy={8} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 11, fontWeight: 600 }} tickFormatter={(v) => `${currencySymbol}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={50} />
                    <Tooltip
                      formatter={(v) => fmtCurrency(v, orgCurrency)}
                      contentStyle={{ borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)", fontSize: 12, padding: "8px 12px" }}
                      cursor={{ stroke: "var(--color-border-subtle)" }}
                    />
                    <Area type="monotone" dataKey="value" stroke="var(--crm-primary)" strokeWidth={2.5} fill="url(#revGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Lead sources" subtitle="Distribution" height={300}>
                {leadSources.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--color-text-secondary)", fontSize: 12 }}>No data yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={leadSources} cx="50%" cy="45%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                        {leadSources.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)", fontSize: 12, padding: "8px 12px" }} />
                      <Legend layout="horizontal" verticalAlign="bottom" align="center" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "stretch" }}>
              <ChartCard title="Pipeline summary" subtitle="Open leads by stage" height={Math.max(220, pipelineStages.length * 36 + 60)}>
                {pipelineStages.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--color-text-secondary)", fontSize: 12 }}>No pipeline data</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pipelineStages} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border-subtle)" />
                      <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 11, fontWeight: 600 }} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-primary)", fontSize: 12, fontWeight: 600 }} width={110} />
                      <Tooltip cursor={{ fill: "var(--color-bg-muted)" }} contentStyle={{ borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)", fontSize: 12, padding: "8px 12px" }} />
                      <Bar dataKey="value" fill="var(--crm-primary)" radius={[0, 6, 6, 0]} barSize={22} label={{ position: "right", fill: "var(--color-text-secondary)", fontSize: 11, fontWeight: 600 }} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 18, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <div style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Scheduled meetings</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>Upcoming</div>
                  </div>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--crm-success-soft, rgba(16,185,129,0.12))", color: "var(--crm-success, rgb(16,185,129))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Video size={15} />
                  </div>
                </div>
                {upcomingMeetings.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--color-text-secondary)", fontSize: 13, padding: 24 }}>
                    No upcoming meetings.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {upcomingMeetings.map((m) => {
                      const fullName = (u) => {
                        if (!u) return null;
                        const n = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
                        return n || u.email || null;
                      };
                      const ownerName = fullName(m.leadOwner);
                      const creatorName = fullName(m.createdBy);
                      return (
                        <div key={m._id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 8, border: "1px solid var(--color-border-subtle)" }}>
                          <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--crm-primary-soft)", color: "var(--crm-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Calendar size={16} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 11, color: "var(--color-text-secondary)", flexWrap: "wrap" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                <Clock size={11} /> {formatMeetingTime(m.scheduledStart, m.scheduledEnd)}
                              </span>
                              {m.lead?.title && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>· {m.lead.title}</span>}
                              {m.project?.name && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>· {m.project.name}</span>}
                            </div>
                            {(ownerName || creatorName) && (
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, fontSize: 11, color: "var(--color-text-secondary)", flexWrap: "wrap" }}>
                                {ownerName && <span>Owner: {ownerName}</span>}
                                {creatorName && creatorName !== ownerName && <span>· Created by {creatorName}</span>}
                              </div>
                            )}
                          </div>
                          {m.meetUri && (
                            <a
                              href={m.meetUri}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ height: 30, padding: "0 12px", borderRadius: 7, background: "var(--crm-success, rgb(16,185,129))", color: "#fff", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none", flexShrink: 0 }}
                            >
                              <Video size={12} /> Join
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Recent activity</div>
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{recentActivities.length}</div>
              </div>
              {recentActivities.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No activity yet.</div>
              ) : (
                recentActivities.map((a) => <ActivityRow key={a._id} activity={a} />)
              )}
            </section>
          </div>
        )}
      </div>

      <EmailFollowUpAdminDrawer open={monitorOpen} onClose={() => setMonitorOpen(false)} />

      <AntDrawer
        open={idleDrawerOpen}
        onClose={() => setIdleDrawerOpen(false)}
        placement="right"
        width={420}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9, flexShrink: 0,
              background: "linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(245,158,11,0.12) 100%)",
              color: "rgb(220,38,38)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Clock size={15} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: 1.2 }}>Idle Leads</div>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 400 }}>Time since last stage move</div>
            </div>
          </div>
        }
        styles={{
          header: { borderBottom: "1px solid var(--color-border-subtle)", padding: "16px 20px" },
          body: { padding: 20, background: "var(--color-bg-base)" },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Summary line */}
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
            <AlertCircle size={12} />
            {staleLeads.total3 + staleLeads.total7 + staleLeads.total14} open leads haven't moved stages recently
          </div>

          {[
            {
              label: "Cooling",
              sub: "3–6 days idle",
              count: staleLeads.total3,
              filter: "3",
              gradient: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
              border: "rgba(245,158,11,0.28)",
              fg: "rgb(161,98,7)",
              countFg: "rgb(120,70,0)",
              dot: "rgb(245,158,11)",
              desc: "Starting to cool off — a quick follow-up can re-engage.",
            },
            {
              label: "Stale",
              sub: "7–13 days idle",
              count: staleLeads.total7,
              filter: "7",
              gradient: "linear-gradient(135deg, #fff5f5 0%, #fee2e2 100%)",
              border: "rgba(239,68,68,0.25)",
              fg: "rgb(185,28,28)",
              countFg: "rgb(153,27,27)",
              dot: "rgb(239,68,68)",
              desc: "At risk of going cold — prioritise outreach this week.",
            },
            {
              label: "Critical",
              sub: "14+ days idle",
              count: staleLeads.total14,
              filter: "14",
              gradient: "linear-gradient(135deg, #fff1f2 0%, #fecdd3 100%)",
              border: "rgba(220,38,38,0.30)",
              fg: "rgb(159,18,57)",
              countFg: "rgb(136,19,55)",
              dot: "rgb(220,38,38)",
              desc: "Likely cold — review and either re-engage or mark lost.",
            },
          ].map(({ label, sub, count, filter, gradient, border, fg, countFg, dot, desc }) => (
            <motion.button
              key={filter}
              whileHover={{ y: -2, boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
              whileTap={{ scale: 0.98 }}
              transition={spring.quick}
              onClick={() => { setIdleDrawerOpen(false); onNavigateToPipeline?.(filter); }}
              style={{
                width: "100%", textAlign: "left",
                background: gradient,
                border: `1px solid ${border}`,
                borderRadius: 8,
                padding: "16px 18px",
                display: "flex", alignItems: "center", gap: 16,
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                position: "relative", overflow: "hidden",
              }}
            >
              {/* Blurred glow */}
              <div style={{
                position: "absolute", right: -20, top: -20,
                width: 80, height: 80, borderRadius: "50%",
                background: dot, opacity: 0.09, filter: "blur(20px)",
                pointerEvents: "none",
              }} />

              {/* Count block */}
              <div style={{ flexShrink: 0, textAlign: "center", minWidth: 56 }}>
                <div style={{ fontSize: 36, fontWeight: 600, color: countFg, fontVariantNumeric: "tabular-nums", lineHeight: 1, letterSpacing: "-0.02em" }}>
                  {count}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: fg, opacity: 0.7, marginTop: 2 }}>leads</div>
              </div>

              {/* Divider */}
              <div style={{ width: 1, alignSelf: "stretch", background: border, flexShrink: 0 }} />

              {/* Text block */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, display: "inline-block", flexShrink: 0, boxShadow: `0 0 0 2px ${dot}33` }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: fg, }}>{label}</span>
                  <span style={{ fontSize: 11, color: fg, opacity: 0.65, fontWeight: 500, marginLeft: 2 }}>{sub}</span>
                </div>
                <div style={{ fontSize: 12, color: fg, opacity: 0.75, lineHeight: 1.45 }}>{desc}</div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: 16, color: fg, opacity: 0.4, flexShrink: 0 }}>→</div>
            </motion.button>
          ))}

          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 5, marginTop: 4, paddingTop: 12, borderTop: "1px solid var(--color-border-subtle)" }}>
            <Clock size={10} style={{ flexShrink: 0 }} />
            Click a bucket to open the Pipeline filtered to those leads
          </div>
        </div>
      </AntDrawer>
    </div>
  );
}
